import { EmployeeSituation } from "@/constants/employee-situation";
import {
  BANNER_CONFIG,
  BannerVariant,
} from "@/pages/store-back-office/access-request/create/components/types/banner-variant";
import { FormData } from "@/pages/store-back-office/access-request/create/validate-form";
import { ACCESS_REQUEST_MESSAGES } from "@/pages/store-back-office/access-request/data/messages";
import { LogixSuccessResponse } from "@/services/dtos/logix-success-response";
import { useFindUserInLogixWithValidationQuery } from "@/services/logix";
import { useValidateKonvivaTrainingQuery } from "@/services/request-access";
import useCpfMask, { CPF_LENGTH } from "@/utils/cpf-mask";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

interface Banner {
  variant: BannerVariant;
  title: string;
  message: string;
}

interface UseAccessRequestValidationProps {
  formMethods: UseFormReturn<FormData>;
  onValidationChange?: (canSubmit: boolean) => void;
}

/**
 * Flag para bypass da validação Konviva.
 * Quando `true`, pula a chamada ao Konviva e trata como aprovado.
 * ATENÇÃO: Alterar para `false` antes de subir para produção.
 */
const SKIP_KONVIVA_VALIDATION = true;

const INITIAL_BANNER: Banner = {
  variant: BANNER_CONFIG.none.variant,
  title: "",
  message: "",
};

export const useAccessRequestValidation = ({
  formMethods,
  onValidationChange,
}: UseAccessRequestValidationProps) => {
  const { setValue, reset } = formMethods;
  const {
    cpfMask,
    isValid: isCpfValid,
    handleCpfChange,
    cpf,
    cleanCpf,
  } = useCpfMask();

  const cleanedCpf = cleanCpf(cpf);
  const isCpfReady =
    isCpfValid && cleanedCpf.length === CPF_LENGTH.WITHOUT_MASK;

  const {
    data: konvivaData,
    error: konvivaError,
    isFetching: isFetchingKonviva,
  } = useValidateKonvivaTrainingQuery(cleanedCpf, {
    skip: !isCpfReady || SKIP_KONVIVA_VALIDATION,
    refetchOnReconnect: false,
    refetchOnFocus: false,
  });

  const isKonvivaApproved =
    SKIP_KONVIVA_VALIDATION || konvivaData?.approved === true;

  const {
    data: logixData,
    error: logixError,
    isFetching: isFetchingLogix,
  } = useFindUserInLogixWithValidationQuery(
    isCpfReady && isKonvivaApproved ? cpf : skipToken,
    { refetchOnReconnect: false, refetchOnFocus: false },
  );

  const isLoading = isFetchingKonviva || isFetchingLogix;

  const { banner, canSubmit, isDismissed } = useMemo(() => {
    if (!isCpfReady) {
      return { banner: INITIAL_BANNER, canSubmit: false, isDismissed: false };
    }

    if (!SKIP_KONVIVA_VALIDATION && konvivaError) {
      return {
        banner: {
          variant: BANNER_CONFIG.error.variant,
          title: ACCESS_REQUEST_MESSAGES.BANNER.ERROR.title,
          message: "Serviço Konviva indisponível. Tente novamente mais tarde.",
        },
        canSubmit: false,
        isDismissed: false,
      };
    }

    if (!SKIP_KONVIVA_VALIDATION && konvivaData && !konvivaData.approved) {
      const cursosPendentes = konvivaData.pendingCourses
        .map((course) => `• ${course.name} - ${course.status}`)
        .join("\n");

      return {
        banner: {
          variant: BANNER_CONFIG.warning.variant,
          title: ACCESS_REQUEST_MESSAGES.BANNER.PENDING_TRAINING.title,
          message:
            ACCESS_REQUEST_MESSAGES.BANNER.PENDING_TRAINING.message(
              cursosPendentes,
            ),
        },
        canSubmit: false,
        isDismissed: false,
      };
    }

    if (logixError) {
      const status =
        (logixError as any)?.status ?? (logixError as any)?.originalStatus;
      const msg = extractRtkErrorMessage(
        logixError,
        ACCESS_REQUEST_MESSAGES.BANNER.ERROR.message,
      );

      if (status === 403) {
        return {
          banner: {
            variant: BANNER_CONFIG.warning.variant,
            title: ACCESS_REQUEST_MESSAGES.BANNER.OFFICE_EMPLOYEE.title,
            message:
              msg || ACCESS_REQUEST_MESSAGES.BANNER.OFFICE_EMPLOYEE.message,
          },
          canSubmit: false,
          isDismissed: false,
        };
      }

      return {
        banner: {
          variant: BANNER_CONFIG.error.variant,
          title: ACCESS_REQUEST_MESSAGES.BANNER.ERROR.title,
          message:
            status === 404
              ? msg || ACCESS_REQUEST_MESSAGES.BANNER.NOT_FOUND.message
              : msg,
        },
        canSubmit: false,
        isDismissed: false,
      };
    }

    if (logixData) {
      const resp = logixData as LogixSuccessResponse;
      const isDismissedStatus =
        resp.user.situation === EmployeeSituation.DISMISSED;

      if (isDismissedStatus) {
        return {
          banner: {
            variant: BANNER_CONFIG.dismissed.variant,
            title: ACCESS_REQUEST_MESSAGES.BANNER.DISMISSED.title,
            message: ACCESS_REQUEST_MESSAGES.BANNER.DISMISSED.message,
          },
          canSubmit: false,
          isDismissed: true,
        };
      }

      if (!resp.validation.canCreateOperator) {
        return {
          banner: {
            variant: BANNER_CONFIG.warning.variant,
            title: ACCESS_REQUEST_MESSAGES.BANNER.BLOCKED.title,
            message:
              resp.validation.message ||
              ACCESS_REQUEST_MESSAGES.BANNER.BLOCKED.message,
          },
          canSubmit: false,
          isDismissed: false,
        };
      }

      return {
        banner: {
          variant: BANNER_CONFIG.success.variant,
          title: ACCESS_REQUEST_MESSAGES.BANNER.SUCCESS.title,
          message:
            resp.validation.message ||
            ACCESS_REQUEST_MESSAGES.BANNER.SUCCESS.message,
        },
        canSubmit: true,
        isDismissed: false,
      };
    }

    return { banner: INITIAL_BANNER, canSubmit: false, isDismissed: false };
  }, [isCpfReady, konvivaData, konvivaError, logixData, logixError]);

  useEffect(() => {
    if (!isCpfReady) {
      reset({
        name: "",
        cpf: "",
        cost_center: "",
        store: "",
        cargo: "",
        matricula: "",
        situation: "",
      });
      onValidationChange?.(false);
      return;
    }

    if (logixData) {
      const resp = logixData as LogixSuccessResponse;
      setValue("name", resp.user.name);
      setValue("cpf", resp.user.cpf);
      setValue("cost_center", resp.user.cost_center);
      setValue("store", String(resp.user.company));
      setValue("cargo", resp.user.position);
      setValue("matricula", String(resp.user.register));
      setValue("situation", resp.user.situation);
    }

    onValidationChange?.(canSubmit);
  }, [isCpfReady, logixData, canSubmit, reset, setValue, onValidationChange]);

  return {
    cpfMask,
    handleCpfChange,
    isCpfValid,
    banner,
    canSubmit,
    isLoading,
    isDismissed,
  };
};
